import { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Chip,
    IconButton,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    useTheme,
} from '@mui/material';
import { FilterList, Upload } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import ListAnalysis from './ListAnalysis';
import { getAllCourses } from '../../../API/Course';
import { useReactToPrint } from 'react-to-print';
import { getAllUsers } from '../../../API/getUsers';
import * as XLSX from 'xlsx';

const Content = ({ users }) => {
    const theme = useTheme();
    const [User, setUser] = useState('student');
    const [courses, setCourses] = useState([]);
    const [data, setData] = useState([]);

    // Server-side pagination states
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(5);
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(false);
    // const [exportData, setExportData] = useState([]);

    const handleChange = (event) => {
        setUser(event.target.value);
        setPage(0);
    };

    const formatDuration = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const formattedDuration = `${hours}h ${minutes}m`;
        return formattedDuration;
    };

    const courseColumn = [
        {
            field: 'courseName',
            headerName: 'Name',
            flex: 1,
        },
        {
            field: 'createdAt',
            headerName: 'Created On',
            flex: 1,
            valueFormatter: (params) => {
                // Assuming updatedAt is a valid date string
                const date = new Date(params.value);
                return date.toLocaleString(); // Adjust the formatting as needed
            },
        },
        {
            field: 'duration',
            headerName: 'Course Duration',
            flex: 1,
            valueFormatter: (params) => {
                const durationInSeconds = params.value;
                const formattedDuration = formatDuration(durationInSeconds);
                return formattedDuration;
            },
        },
        {
            field: 'subCategory',
            headerName: 'Sub Category',
            flex: 1,
        },
        {
            field: 'majorCategory',
            headerName: 'Major Category',
            flex: 1,
        },
    ];

    const columns = [
        // {
        //     field: '_id',
        //     headerName: 'Id.',
        //     renderCell: (params) => {
        //         params.api.getRowIndex(params.row.id) + 1;
        //     },
        //     flex: 1.5
        // },
        {
            field: '_id',
            headerName: 'Id.',
            renderCell: (params) => {
                return params.api.getRowIndex(params.row._id) + 1;
            },
            flex: 1,
        },
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
        },
        {
            field: 'userName',
            headerName: 'User Name',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            flex: 1,
        },

        {
            field: 'email',
            headerName: 'Email',
            align: 'center',
            type: 'number',
            headerAlign: 'center',
            flex: 1.5,
        },
        {
            field: 'majorCategory',
            headerName: 'Major Category',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            flex: 1,
        },
        {
            field: 'subCategory',
            headerName: 'Sub Category',
            flex: 1,
            headerAlign: 'center',
            align: 'center',
        },
    ];

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            if (users) {
                const response = await getAllUsers(page + 1, pageSize, User);
                console.log('ress', response);
                setData(response?.users || []);
                setRowCount(response?.total || 0);
            } else {
                const response = await getAllCourses(page + 1, pageSize);
                console.log('courses', response);
                setCourses(response?.courses || []);
                setRowCount(response?.total || 0);
            }
        } catch (error) {
            console.error('Error fetching paginated data:', error);
        } finally {
            setLoading(false);
        }
    }, [users, User, page, pageSize]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // useEffect(() => {
    //     if (users) {
    //         getAllUsers(null, null, User).then((res) => {
    //             setExportData(res?.users || []);
    //         });
    //     } else {
    //         getAllCourses().then((res) => {
    //             setExportData(res || []);
    //         });
    //     }
    // }, [users, User]);

    const excelTableRef = useRef();

    const onDownload = () => {
        if (!excelTableRef.current) return;
        const workbook = XLSX.utils.table_to_book(excelTableRef.current, {
            sheet: users ? 'Users' : 'Courses',
        });
        XLSX.writeFile(
            workbook,
            users ? `${User}s_list.xlsx` : 'Courses_list.xlsx',
        );
    };

    return (
        <Box px={{ xs: 4.5, md: 9 }}>
            <Box display="flex" justifyContent="flex-end">
                <Box display="flex">
                    {users && (
                        <FormControl fullWidth>
                            <InputLabel id="demo-simple-select-label">
                                User Type
                            </InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                size="small"
                                value={User}
                                sx={{ width: 139 }}
                                label="User Type"
                                onChange={handleChange}
                            >
                                <MenuItem value="student">Student</MenuItem>
                                <MenuItem value="instructor">
                                    Instructor
                                </MenuItem>
                                {/* <MenuItem value="labAssistant">
                                    Lab Assistant
                                </MenuItem> */}
                            </Select>
                        </FormControl>
                    )}
                </Box>
            </Box>
            <Box mt={4}>
                <IconButton
                    color="secondary"
                    sx={{ mr: 4 }}
                    onClick={onDownload}
                >
                    <Upload sx={{ mr: 1 }} />
                    EXPORT
                </IconButton>
                <Box height={400} width="100%" mt={1}>
                    <DataGrid
                        rows={users ? data : courses}
                        columns={users ? columns : courseColumn}
                        getRowId={(rows) => rows?._id}
                        disableSelectionOnClick
                        pagination
                        paginationMode="server"
                        rowCount={rowCount}
                        pageSize={pageSize}
                        onPageSizeChange={(newPageSize) =>
                            setPageSize(newPageSize)
                        }
                        page={page}
                        onPageChange={(newPage) => setPage(newPage)}
                        loading={loading}
                        rowsPerPageOptions={[5, 10, 20]}
                        sx={{
                            [theme.breakpoints.down('sm')]: {
                                display: 'none',
                            },
                        }}
                    />
                    <ListAnalysis data={data} users={users && users} />
                </Box>
            </Box>

            <table ref={excelTableRef} style={{ display: 'none' }}>
                <thead>
                    {users ? (
                        <tr>
                            <th>Id.</th>
                            <th>Name</th>
                            <th>User Name</th>
                            <th>Email</th>
                            <th>Major Category</th>
                            <th>Sub Category</th>
                        </tr>
                    ) : (
                        <tr>
                            <th>Id.</th>
                            <th>Name</th>
                            <th>Created On</th>
                            <th>Course Duration</th>
                            <th>Sub Category</th>
                            <th>Major Category</th>
                        </tr>
                    )}
                </thead>
                <tbody>
                    {users
                        ? data.map((row, index) => (
                              <tr key={row._id || index}>
                                  <td>{index + 1}</td>
                                  <td>{row.name || ''}</td>
                                  <td>{row.userName || ''}</td>
                                  <td>{row.email || ''}</td>
                                  <td>{row.majorCategory || ''}</td>
                                  <td>{row.subCategory || ''}</td>
                              </tr>
                          ))
                        : courses.map((row, index) => (
                              <tr key={row._id || index}>
                                  <td>{index + 1}</td>
                                  <td>{row.courseName || ''}</td>
                                  <td>
                                      {row.createdAt
                                          ? new Date(
                                                row.createdAt,
                                            ).toLocaleString()
                                          : ''}
                                  </td>
                                  <td>{formatDuration(row.duration)}</td>
                                  <td>{row.subCategory || ''}</td>
                                  <td>{row.majorCategory || ''}</td>
                              </tr>
                          ))}
                </tbody>
            </table>
        </Box>
    );
};

Content.propTypes = {
    users: PropTypes.bool.isRequired,
};

export default Content;
